import { Component, OnDestroy, OnInit } from "@angular/core";
import { IContentHeader } from "app/layout/components/content-header/content-header.component";
import moment from "moment";
import { ApexAxisChartSeries, ApexTooltip, ApexDataLabels } from "ng-apexcharts";
import { StatsService } from "../../stats.service";
import { AuthenticationService, UserService } from "app/auth/service";
import { UtilsService } from "app/auth/service/utils.service";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, Subscription, throwError } from "rxjs";
import { catchError, takeUntil } from "rxjs/operators";

export type BarsChartOptions = {
  dataLabels: ApexDataLabels;
  tooltip: ApexTooltip;
};

@Component({
  selector: "app-recruitment-stats",
  templateUrl: "./recruitment-stats.component.html",
})
export class RecruitmentStatsComponent implements OnInit, OnDestroy {
  public contentHeader: IContentHeader;
  public fromFilter: string = `${moment().year()}-01-01`;
  public toFilter: string = moment().format("YYYY-MM-DD");
  public recruiterFilter: string = "0";
  public totalStepCounter: { suitables: number; proposed: number; calls: number; ko: number; bargaining: number; hired: number } | undefined;
  public totalInsertedCandidates: number = 0;
  private _unsubscribeAll: Subject<any>;
  public showChart: boolean = true;
  private queryParamsSubscription: Subscription;

  public barsChartOptions: BarsChartOptions;
  public chartSeries: ApexAxisChartSeries | undefined = undefined;

  constructor(
    private _statsService: StatsService,
    private _authService: AuthenticationService,
    private router: Router,
    private _utilService: UtilsService,
    private _activatedRoute: ActivatedRoute,
    public _userService: UserService,
  ) {
    this._unsubscribeAll = new Subject();
    this.contentHeader = {
      headerTitle: "STATISTICS",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/",
          },
          {
            name: "MENU.RECRUITMENT",
            isLink: false,
          },
          {
            name: "STATISTICS",
            isLink: false,
          },
        ],
      },
    };

    this.barsChartOptions = {
      dataLabels: {
        enabled: true,
        textAnchor: "start",
        style: {
          colors: ["#fff"],
        },
        formatter: (val, opt) => `${opt.w.globals.labels[opt.dataPointIndex]}:  ${val}`,
        offsetX: 15,
        dropShadow: {
          enabled: true,
        },
      },
      tooltip: {
        theme: "dark",
        x: {
          show: false,
        },
        y: {
          title: {
            formatter: () => "",
          },
        },
      },
    };
  }

  async ngOnInit() {
    if (!this._authService.hasCapability("show_menu_recruitment_stats")) {
      return this.router.navigateByUrl("/dashboard/main");
    }

    if (!window.location.search.includes("reloadForChart")) {
      const url: string = window.location.href + "?reloadForChart";
      window.history.replaceState({}, "", url);
      window.location.href = url;
    } else {
      if (this._userService.recruiter_list.length < 1) {
        await this._userService.getRecruiterList();
      }

      this._activatedRoute.queryParams.subscribe(params => {
        if (params.from) {
          const from = moment(params.from);
          this.fromFilter = from.isValid() ? from.format("YYYY-MM-DD") : this.fromFilter;
        }

        if (params.to) {
          const to = moment(params.to);
          this.toFilter = to.isValid() ? to.format("YYYY-MM-DD") : this.toFilter;
        }

        if (params.recruiter) {
          this.recruiterFilter = params.recruiter || this.recruiterFilter;
        }

        this.loadStats();
        this.subscribeStats();
      });
    }
  }

  takeDate(event: any, field: string) {
    if (field === "from") {
      this.fromFilter = event;
    }
    if (field === "to") {
      this.toFilter = event;
    }
  }

  loadStats() {
    this.resetStats();
    this._statsService
      .getStats(this.fromFilter, this.toFilter, this.recruiterFilter)
      .pipe(
        takeUntil(this._unsubscribeAll),
        catchError((error) => {
          this._utilService.showSWALError({ text: "ERROR_LOADING_STATS" }).then();
          this.router.navigateByUrl("dashboard/main").then();
          return throwError(error);
        }),
      )
      .subscribe((response: { last_step_counter: { step: number; step_counter: number }[]; total_step_counter: { step: number; step_counter: number }[] }) => {
        this._statsService.onStatsChange.next(response);
      });
  }

  subscribeStats() {
    this.showChart = false;
    this.chartSeries = undefined;
    this._statsService.onStatsChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((item: any) => {
      if (!!item) {
        this.totalStepCounter = {
          suitables: item.suitables,
          proposed: item.proposed,
          calls: item.calls,
          ko: item.ko,
          bargaining: item.bargaining,
          hired: item.hired,
        };

        this.totalInsertedCandidates = item.inserted;

        this.chartSeries = [{ data: Object.values(this.totalStepCounter) }];

        setTimeout(() => {
          this.showChart = true;
          this._utilService.hideLoading();
        });
      }
    });
  }

  resetStats(): void {
    this.chartSeries = undefined;
    this.totalStepCounter = null;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
    if (this.queryParamsSubscription) {
      this.queryParamsSubscription.unsubscribe();
    }
  }

  async filter() {
    if (moment(this.fromFilter) > moment(this.toFilter)) {
      await this._utilService.showSWALError({ text: "FROM_DATE_GREATER_THAN_TO_DATE" });
      return;
    }

    await this.router.navigate([], {
      queryParams: {
        ["from"]: this.fromFilter,
        ["to"]: this.toFilter,
        ["recruiter"]: this.recruiterFilter,
      },
      queryParamsHandling: "merge",
    });
  }
}
